import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { Appointment, Settlement } from "@/api/types";
import { Badge, Button, Field, Input, Select, tx } from "@/components/ui";

type Line = { method: string; amount: string };
type Props = { appointment: Appointment; tz: string; onDone: () => void; onCancel: () => void };
const money = (v: string | number) => (Math.round(Number(v) * 100) / 100).toFixed(2);
const localInput = (iso: string, tz: string) => {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};

/** Settlement of a visit: paid (cash/POS/IRIS, total − deposit), credit (package), trial, complimentary, test visit. */
export function CheckoutDialog({ appointment: a, tz, onDone, onCancel }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["settlement", a.id], queryFn: () => api<Settlement>(`/app/appointments/${a.id}/settlement`) });
  const [type, setType] = useState<string>("paid");
  const [price, setPrice] = useState(money(a.price_quoted));
  const [lines, setLines] = useState<Line[]>([]);
  const [pkgId, setPkgId] = useState("");
  const [when, setWhen] = useState(localInput(new Date().toISOString(), tz));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const s = q.data;
  useEffect(() => {
    if (!s) return;
    const initial = s.service_kind === "trial" ? "trial" : s.package || s.usable_packages.length ? "credit" : "paid";
    setType(initial);
    setPkgId(s.package?.id ?? s.usable_packages[0]?.id ?? "");
    const method = s.payment_methods[0] ?? "cash";
    const due = Math.max(0, Number(a.price_quoted) - Number(s.deposit_paid));
    setLines([{ method, amount: money(due) }]);
  }, [s, a.price_quoted]);
  const deposit = Number(s?.deposit_paid ?? 0);
  const payable = useMemo(() => Math.max(0, Number(price) - deposit), [price, deposit]);
  const sum = lines.reduce((acc, l) => acc + Number(l.amount || 0), 0);
  const diff = Math.round((payable - sum) * 100) / 100;
  const m = useMutation({
    mutationFn: () => api<Settlement>(`/app/appointments/${a.id}/checkout`, { method: "POST", body: JSON.stringify({
      settlement_type: type, price_charged: type === "paid" ? price : null, payments: type === "paid" ? lines.filter((l) => Number(l.amount) > 0) : [],
      customer_package_id: type === "credit" && pkgId ? pkgId : null, checkout_at: when ? new Date(when + ":00").toISOString() : null, note: note || null,
    }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); qc.invalidateQueries({ queryKey: ["checkout-issues"] }); onDone(); },
    onError: (e) => { let reason = e instanceof ApiError ? e.detail : ""; try { reason = JSON.parse(reason).reason; } catch { /* plain */ } setError(t(`checkout.err.${reason}`, { defaultValue: reason || t("app.error") })); },
  });
  if (!s) return <p className="text-sm text-muted">{t("app.loading")}</p>;
  const setLine = (i: number, patch: Partial<Line>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <form onSubmit={(e) => { e.preventDefault(); setError(null); m.mutate(); }} className="space-y-3 rounded-lg border p-3 text-sm">
      <div className="font-medium">{t("checkout.title")}</div>
      <Field label={t("checkout.settlement")}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="paid">{t("checkout.type.paid")}</option>
          <option value="credit" disabled={!s.package && s.usable_packages.length === 0}>{t("checkout.type.credit")}{!s.package && s.usable_packages.length === 0 ? ` (${t("checkout.noCredits")})` : ""}</option>
          <option value="trial">{t("checkout.type.trial")}</option>
          <option value="complimentary">{t("checkout.type.complimentary")}</option>
          {s.can_mark_test_visit && <option value="test_visit">{t("checkout.type.test_visit")}</option>}
        </Select>
      </Field>
      {type === "paid" && (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2 items-end">
            <Field label={t("checkout.price")}><Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
            <div className="text-xs text-muted">{t("checkout.deposit")}<br /><b>{money(deposit)} €</b></div>
            <div className="text-xs text-muted">{t("checkout.payable")}<br /><b>{money(payable)} €</b></div>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <Select className="!w-32" value={l.method} onChange={(e) => setLine(i, { method: e.target.value })}>{s.payment_methods.map((pm) => <option key={pm} value={pm}>{t(`checkout.method.${pm}`)}</option>)}</Select>
              <Input className="!w-28" type="number" step="0.01" min="0" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} />
              {lines.length > 1 && <button type="button" className="text-xs" onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>}
            </div>
          ))}
          <div className="flex items-center gap-3 text-xs">
            <button type="button" className="underline" onClick={() => setLines([...lines, { method: s.payment_methods[1] ?? s.payment_methods[0] ?? "cash", amount: money(Math.max(0, diff)) }])}>{t("checkout.split")}</button>
            {Math.abs(diff) > 0.004 && <Badge tone="amber">{diff > 0 ? t("checkout.missing", { amount: money(diff) }) : t("checkout.over", { amount: money(-diff) })}</Badge>}
          </div>
        </div>
      )}
      {type === "credit" && (
        <Field label={t("checkout.package")}>
          {s.package ? <div className="rounded-lg bg-surface-2 px-3 py-1.5">{tx(s.package.name, i18n.language)} · {t("checkout.creditsLeft", { n: s.package.credits_left, total: s.package.credits_total })} · {t("checkout.alreadyBurned")}</div>
            : <Select value={pkgId} onChange={(e) => setPkgId(e.target.value)}>{s.usable_packages.map((p) => <option key={p.id} value={p.id}>{tx(p.name, i18n.language)} · {p.credits_left} · {new Date(p.expires_at).toLocaleDateString(i18n.language)}</option>)}</Select>}
        </Field>
      )}
      {type === "test_visit" && <p className="text-xs text-warn">{t("checkout.testVisitHint")}</p>}
      <div className="grid grid-cols-2 gap-2">
        <Field label={t("checkout.time")}><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <Field label={t("appt.notes")}><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      {error && <p className="text-danger" role="alert">{error}</p>}
      <div className="flex gap-2">
        <Button disabled={m.isPending || (type === "paid" && Math.abs(diff) > 0.004) || (type === "credit" && !s.package && !pkgId)} title={type === "paid" && Math.abs(diff) > 0.004 ? t("checkout.mustBalance") : undefined}>{t("checkout.confirm")}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>{t("app.cancel")}</Button>
      </div>
    </form>
  );
}
